import { Controller } from "@hotwired/stimulus"
import { enter, leave } from "el-transition"
import { createRiesterChart } from "../charts/riester/chart"
import { seriesBuschutz, seriesLiqui, seriesSteuer } from "../charts/riester/series"

export default class extends Controller {
	static targets = ["data", "notification", "charticon", "chartbutton", "zoombutton", "bruttowertToggle"]
	static values = {
		riesterId: String,
		url: String,
		detailFrameId: String,
	}

	initialize() {
		this.chart = createRiesterChart()
		this.chart.setZoomCallbacks({
			onZoomIn: () => this.showZoombutton(),
			onZoomReset: () => this.hideZoombutton(),
		})
		this.chart.setChartSettingsCallbacks({
			onLegendToggle: (seriesId, willBeVisible) => {
				this.chart.toggleHiddenSeriesLocal(seriesId, willBeVisible)
				this.persistLegendVisibility(seriesId)
			},
		})
	}

	connect() {
		this.berechnungStatusListenerRegistrieren()
		this.chart.data.id = this.riesterIdValue
		this.chart.data.detailUrl = this.urlValue
		this.chart.data.detailFrameId = this.detailFrameIdValue

		if (this.hasDataTarget) this.setChartData()
		this.applyBruttowertToggleFromData()
		this.chart.init()
		this.syncBerechnungLoading()
	}

	disconnect() {
		this.berechnungStatusListenerEntfernen()
	}

	dataTargetConnected() {
		this.setChartData()
		this.applyBruttowertToggleFromData()
		this.chart.update()
		this.aktualisiereLoadingNachChartdaten()
	}

	notificationTargetConnected() {
		this.showBerechnungLoading()
		enter(this.notificationTarget)
	}

	changeChartSeries({ currentTarget, params }) {
		const seriesName =
			params.series || currentTarget?.dataset?.riesterChartSeriesParam || currentTarget?.dataset?.chartSeriesParam

		if (this.chartbuttonTargets.includes(currentTarget)) {
			this.chartbuttonTargets.forEach((e) => e.removeAttribute("data-ui"))
			currentTarget.dataset.ui = "aktiv"
		} else {
			this.charticonTargets.forEach((e) => e.classList.toggle("hidden"))
		}

		switch (seriesName) {
			case "steuer":
				this.chart.updateSeriesData(seriesSteuer(this.chart.data), "steuer")
				break
			case "liqui":
				this.chart.updateSeriesData(seriesLiqui(this.chart.data), "liqui")
				break
			case "buschutz":
				this.chart.updateSeriesData(seriesBuschutz(this.chart.data), "buschutz")
				break
			default:
				console.error("Keine Series im Chart gefunden")
				this.charticonTargets.forEach((e) => e.classList.toggle("hidden"))
		}
	}

	resetZoom() {
		if (!this.chart?.chart) return
		this.chart.data.anzeigeformat = "jahr"
		this.chart.chart.zoomOut()
	}

	changeBruttowertAnsicht({ currentTarget }) {
		if (!this.chart?.chart) return

		this.chart.data.status = currentTarget.checked ? "konsolidiert" : "differenziert"
		if (this.chart.data.chartType === "liqui") {
			this.chart.updateSeriesData(seriesLiqui(this.chart.data), "liqui")
		}
		this.persistBruttowertAnsicht(this.chart.data.status)
	}

	// --- private

	setChartData() {
		const incoming = JSON.parse(this.dataTarget.dataset.chartdata)
		Object.assign(this.chart.data, incoming)
		this.chart.data.charteinstellungen ||= { hidden_series: [], bruttowert_ansicht: "konsolidiert" }
		this.chart.data.status ||= this.chart.data.charteinstellungen.bruttowert_ansicht || "konsolidiert"
	}

	applyBruttowertToggleFromData() {
		if (!this.hasBruttowertToggleTarget) return
		this.bruttowertToggleTarget.checked = this.chart?.data?.status === "konsolidiert"
	}

	berechnungStatusListenerRegistrieren() {
		if (this.berechnungStatusListener) return

		this.berechnungStatusListener = (event) => this.berechnungStatusGeaendert(event)
		document.addEventListener("berechnung-status:geaendert", this.berechnungStatusListener)
	}

	berechnungStatusListenerEntfernen() {
		if (!this.berechnungStatusListener) return

		document.removeEventListener("berechnung-status:geaendert", this.berechnungStatusListener)
		this.berechnungStatusListener = null
	}

	berechnungStatusGeaendert(event) {
		if (!this.lokalerBerechnungStatus(event)) return

		if (event.detail.status === "laufend") {
			this.showBerechnungLoading()
		} else {
			this.hideBerechnungLoading()
		}
	}

	lokalerBerechnungStatus(event) {
		return event.target && typeof this.element.contains === "function" && this.element.contains(event.target)
	}

	syncBerechnungLoading() {
		if (this.berechnungLaeuft()) {
			this.showBerechnungLoading()
		} else {
			this.hideBerechnungLoading()
		}
	}

	aktualisiereLoadingNachChartdaten() {
		if (this.hasNotificationTarget) {
			this.chartdatenVerbunden = true
			this.hideNotification()
			return
		}

		if (this.chartdatenErstmalsVerbunden()) {
			this.syncBerechnungLoading()
			return
		}

		this.hideNotification()
	}

	chartdatenErstmalsVerbunden() {
		const erstmals = !this.chartdatenVerbunden
		this.chartdatenVerbunden = true
		return erstmals
	}

	berechnungLaeuft() {
		return (
			this.element.querySelector('[data-controller~="berechnung-status"][data-berechnung-status="laufend"]') !==
			null
		)
	}

	showBerechnungLoading() {
		if (this.chart?.chart) this.chart.showLoading()
	}

	hideBerechnungLoading() {
		if (this.chart?.chart) this.chart.hideLoading()
	}

	hideNotification() {
		if (!this.hasNotificationTarget) {
			this.hideBerechnungLoading()
			return
		}

		leave(this.notificationTarget).then(() => {
			this.hideBerechnungLoading()
			this.notificationTargets.forEach((element) => element.remove())
		})
	}

	hideZoombutton() {
		if (this.hasZoombuttonTarget) leave(this.zoombuttonTarget)
	}

	showZoombutton() {
		if (this.hasZoombuttonTarget) enter(this.zoombuttonTarget)
	}

	persistLegendVisibility(seriesId) {
		const token = document.querySelector('meta[name="csrf-token"]')?.content
		if (!token) return

		fetch(`/riesters/${this.riesterIdValue}/charteinstellungen`, {
			method: "PATCH",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				"X-CSRF-Token": token,
			},
			body: JSON.stringify({ series_id: seriesId }),
			credentials: "same-origin",
		}).catch((e) => console.error("Chart-Einstellungen konnten nicht gespeichert werden:", e))
	}

	persistBruttowertAnsicht(status) {
		const token = document.querySelector('meta[name="csrf-token"]')?.content
		if (!token) return

		fetch(`/riesters/${this.riesterIdValue}/charteinstellungen`, {
			method: "PATCH",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				"X-CSRF-Token": token,
			},
			body: JSON.stringify({ bruttowert_ansicht: status }),
			credentials: "same-origin",
		}).catch((e) => console.error("Chart-Einstellungen konnten nicht gespeichert werden:", e))
	}
}
